import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, InputField } from '@pickleballinc/react-ui';
import { forwardRef } from 'react';

import { MobileFilterRow } from '@/modules/rankings/MobileFilter/MobileFilterRow';
import { PlayerFiltersInterface } from '@/types/ranking.types';

interface CountryMobileProps {
  country: {
    id: number;
    title: string;
  };
  countryDropdownOpen: boolean;
  toggleCountryDropdown: () => void;
  countryDropdownRef: React.RefObject<HTMLDivElement | null>;
  countriesList: Array<{ id: number; title: string }>;
  filters: PlayerFiltersInterface;
  setFilters: React.Dispatch<React.SetStateAction<PlayerFiltersInterface>>;
  searchInput: string;
  handleSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleClickCancel: () => void;
  handleClickNext: () => void;
}

const CountryMobile = forwardRef<HTMLDivElement, CountryMobileProps>(
  (
    {
      country,
      countryDropdownOpen,
      toggleCountryDropdown,
      countryDropdownRef,
      countriesList,
      filters,
      setFilters,
      searchInput,
      handleSearchInputChange,
      handleClickCancel,
      handleClickNext,
    },
    ref
  ) => {
    return (
      <div ref={ref}>
        {!countryDropdownOpen ? (
          <MobileFilterRow
            label='Country'
            value={country.title}
            onClick={toggleCountryDropdown}
            testId='rankings-country-button'
          />
        ) : (
          <div
            className='mx-auto w-full max-w-md rounded-lg border border-gray-200 bg-white p-4 shadow-md'
            ref={countryDropdownRef}
          >
            <h2 className='mb-4 text-lg font-semibold' onClick={toggleCountryDropdown}>
              Country
            </h2>
            <div className='relative w-full p-3'>
              <InputField
                autoFocus
                placeholder='Search Countries'
                value={searchInput}
                onChange={handleSearchInputChange}
                PrefixIcon={() => <FontAwesomeIcon icon={faMagnifyingGlass} />}
                className='w-full rounded-md border p-1 text-xl'
                data-testid='rankings-country-search'
              />
            </div>
            <div className='mb-4 max-h-[45vh] space-y-2 overflow-y-auto rounded-md border border-gray-200'>
              {countriesList
                .filter((option) => option.title.toLowerCase().includes(searchInput.toLowerCase()))
                .map((option) => (
                  <div
                    key={`country-rankings-${String(option.id)}`}
                    onClick={() =>
                      setFilters({
                        ...filters,
                        country: String(option.id),
                        state: filters.country === String(option.id) ? filters.state : '',
                      })
                    }
                    className={`m-1 flex cursor-pointer justify-between rounded-lg p-3 text-gray-900 ${
                      filters.country === String(option.id) ? 'bg-gray-50' : 'bg-white'
                    }`}
                    data-testid={`rankings-country-option-${option.title}`}
                  >
                    {option.title}
                    {filters.country === String(option.id) && (
                      <span className='ml-2 text-purple-600'>
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    )}
                  </div>
                ))}
            </div>
            <div className='flex justify-between gap-2'>
              <Button
                className='flex w-[150px] items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100'
                onClick={handleClickCancel}
                variant='secondary'
                data-testid='rankings-country-button-cancel'
              >
                Cancel
              </Button>
              <Button
                className='flex w-[150px] items-center justify-center rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700'
                onClick={handleClickNext}
                variant='primary'
                data-testid='rankings-country-button-next'
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

// Add display name for better debugging
CountryMobile.displayName = 'CountryMobile';

export default CountryMobile;
